// Tokens live in a non-httpOnly cookie (always) and in localStorage (only
// when "Remember me" is ticked); localStorage wins on read. Moving to
// httpOnly cookies needs a backend change -- see README.

export interface AuthToken {
  access_token: string;
  expires_in: number;
  refresh_token: string;
}

const ACCESS = "access_token";
const REFRESH = "refresh_token";
const USERNAME = "username";

/**
 * Both cookies live as long as the refresh token the server mints (30 days;
 * the response does not report it). The access token's own `exp` says whether
 * it is still good -- a stale one just triggers the refresh grant -- so its
 * cookie must outlive `expires_in` or a reload after an hour ends the session.
 */
const COOKIE_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;

function setCookie(name: string, value: string, expiresAtMs: number): void {
  const expires = new Date(expiresAtMs).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
}

function readCookie(name: string): string | null {
  const parts = `; ${document.cookie}`.split(`; ${name}=`);
  if (parts.length !== 2) return null;
  const raw = parts.pop()?.split(";").shift();
  return raw ? decodeURIComponent(raw) : null;
}

function clearCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return readLocal(ACCESS) ?? readCookie(ACCESS);
}

export function getRefreshToken(): string | null {
  return readLocal(REFRESH) ?? readCookie(REFRESH);
}

/** True when "Remember me" put the tokens in localStorage. */
export function isRemembered(): boolean {
  return readLocal(REFRESH) !== null;
}

export function saveToken(token: AuthToken, rememberMe = false): void {
  const expireDate = Date.now() + COOKIE_LIFETIME_MS;
  setCookie(ACCESS, token.access_token, expireDate);
  setCookie(REFRESH, token.refresh_token, expireDate);
  if (rememberMe) {
    try {
      localStorage.setItem(ACCESS, token.access_token);
      localStorage.setItem(REFRESH, token.refresh_token);
    } catch {
      // Private-mode / quota failures must not break login; the cookie stands.
    }
  }
}

export function clearToken(): void {
  clearCookie(ACCESS);
  clearCookie(REFRESH);
  try {
    localStorage.removeItem(ACCESS);
    localStorage.removeItem(REFRESH);
    localStorage.removeItem(USERNAME);
  } catch {
    // ignore
  }
}

export function getCachedUserName(): string | null {
  return readLocal(USERNAME);
}

export function setCachedUserName(name: string): void {
  try {
    localStorage.setItem(USERNAME, name);
  } catch {
    // ignore
  }
}
